import { useCallback, useRef } from 'react';
import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import type { DiscardedCardsResponse, PlanActionOut, PlanNodeOut } from '@/client';
import {
	getCardTimelineV2HousesHouseIdMaintenanceTimelineGetQueryKey,
	getDiscardedCardsV2HousesHouseIdMaintenanceDiscardedGetInfiniteQueryKey,
	getMaintenancePlanV2HousesHouseIdMaintenancePlanGetOptions,
} from '@/client/@tanstack/react-query.gen';
import axiosInstance from '@/lib/axiosInstance';
import { toaster } from '@/components/ui/toaster';
import { makeLogger } from '@/utils/logger/logger';
import {
	canSnoozeUntil,
	hasEvidence,
	OCCURRENCE_SCOPED_ACTIONS,
	type PlanActionEvidence,
	type PlanActionIntent,
	planActionFormData,
	planActionRequest,
	planRequestIdentity,
} from '@/components/maintenance/model/planActions';
import { planNodes, walk } from '@/components/maintenance/model/planSelectors';
import { PlanActions } from '@/components/maintenance/model/planVocabulary';
import { formatLocalDateForApi } from '@/utils/dateUtils';
import { usePlanRefresh } from '@/hooks/maintenance/usePlanRefresh';
import { DISCARDED_PAGE_LIMIT } from '@/hooks/maintenance/useDiscardedCards';

const logger = makeLogger('usePlanAction');

const planActionsUrl = (houseId: number) => `/v2/houses/${houseId}/maintenance/actions`;

/** The verbs that put a card in the archive, whose list is then out of date. */
const ARCHIVING_ACTIONS: ReadonlySet<string> = new Set([
	PlanActions.Delete,
	PlanActions.Dismiss,
	PlanActions.BulkDismiss,
]);

/**
 * What the endpoint said, where it said anything beyond the status.
 *
 * The plan's write contract is still settling, and a refusal a household cannot read is a refusal
 * only a developer with the network tab open can act on. FastAPI answers with `detail`; anything
 * else falls back to the status, which at least separates "refused" from "never arrived".
 */
const refusalDetail = (error: unknown): string | undefined => {
	const response = (error as { response?: { status?: number; data?: unknown } } | null | undefined)?.response;
	if (!response) return undefined;

	const detail = (response.data as { detail?: unknown } | undefined)?.detail;
	if (typeof detail === 'string' && detail.trim()) return `${response.status}: ${detail.trim()}`;

	return response.status ? `The server answered ${response.status}.` : undefined;
};

/** A 422 rejects the request during resolution/validation, before an action can be written. */
const isPreWriteRejection = (error: unknown): boolean =>
	typeof error === 'object' &&
	error !== null &&
	'response' in error &&
	(error as { response?: { status?: number } }).response?.status === 422;

interface PlanActionOptions {
	/**
	 * Re-read the plan after each action lands. On by default, because a card is what changed and
	 * nothing else can say what it now looks like.
	 *
	 * A caller sending many actions in a row turns it off and refreshes once when it is done. The
	 * review pass is the case: a hundred and fifty-eight verdicts would otherwise be a hundred and
	 * fifty-eight reads of the whole plan, each one redrawing the list the reader is clicking on.
	 * Whoever turns it off owns calling `usePlanRefresh` itself — leaving the screen stale is the
	 * bug this default exists to prevent.
	 */
	refreshOnSuccess?: boolean;
}

interface PlanActionVariables {
	node: PlanNodeOut;
	intent: PlanActionIntent;
	evidence?: PlanActionEvidence;
	clientToken?: string;
}

/**
 * Post one action against one plan card.
 *
 * Written on the axios instance rather than the generated mutation, which is the exception this
 * codebase allows for a complex content type and not a shortcut: `/actions` is content-negotiated —
 * JSON for a file-less action, multipart once a comment or attachment rides along — so FastAPI
 * cannot describe one request body for it and the OpenAPI operation declares none. The generated
 * `postPlanAction…` type is therefore `body?: never`: it can address the endpoint and cannot say
 * anything to it. The response is still the generated `PlanActionOut`.
 *
 * One in-flight action at a time, which the drawer implies — a sheet is open, its submit is
 * disabled while pending — so this hook holds one mutation rather than one per verb.
 *
 * A caller sending several at once may still supply its own `clientToken` per request, and then owns
 * holding it across that request's retries. The review pass does; see `reviewQueueState`. It gains
 * nothing from the hook's own key — that key is remembered for one request at a time, so a queue
 * moving on to the next verdict would find it already replaced.
 */
export const usePlanAction = (houseId: number | undefined, options: PlanActionOptions = {}) => {
	const { refreshOnSuccess = true } = options;
	const refreshPlan = usePlanRefresh(houseId);
	const queryClient = useQueryClient();

	/**
	 * The idempotency key, and the request it was minted for.
	 *
	 * The endpoint holds a key to the first request sent under it: the same key with the same request
	 * replays the stored response rather than writing twice, and the same key with a *different*
	 * request is a 409. Both halves matter to a sheet that stays open on failure holding its draft — a
	 * second tap has to replay, and a retry after the household edits that draft has to be allowed to
	 * write. So the key is held for as long as the request is identical and re-minted the moment it is
	 * not, which is why it is keyed on the request rather than on the attempt.
	 */
	const attemptRef = useRef<{ identity: string; clientToken: string } | null>(null);

	/** The key this request travels under: the one already held for it, or a new one. */
	const keyFor = (identity: string): string => {
		if (attemptRef.current?.identity === identity) return attemptRef.current.clientToken;

		const clientToken = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
		attemptRef.current = { identity, clientToken };

		return clientToken;
	};

	const mutation = useMutation({
		mutationFn: async ({ node, intent, evidence, clientToken }: PlanActionVariables) => {
			if (houseId === undefined) throw new Error('No property selected');
			// Validate every send, including the canonical custom-card retry. A date that was forward
			// on the original projection may be backwards once its current occurrence is resolved.
			if (intent.action === PlanActions.Snooze && !canSnoozeUntil(node, intent.until, formatLocalDateForApi())) {
				throw new Error('Defer must move the current occurrence to a later date');
			}

			// The key is chosen from the request, so the request is built first and stamped after. The
			// placeholder below is never sent — `planRequestIdentity` ignores it, and it is replaced.
			const unstamped = planActionRequest(node, intent, '');
			if (!unstamped) throw new Error(`This card cannot take the "${intent.action}" action`);

			// Evidence is anchored to an occasion, so it only travels with an occurrence-scoped action;
			// attaching it to anything else is a 422 rather than a silent misfiling. Dropping it here
			// would be worse than the refusal, so an obligation-level action simply never collects any.
			// Decided before the key is chosen, so the key is keyed on what is actually sent: evidence
			// the request drops must not make two identical bodies look like two different requests.
			const carried =
				hasEvidence(evidence) && OCCURRENCE_SCOPED_ACTIONS.has(intent.action) ? evidence : undefined;

			const request = {
				...unstamped,
				client_token: clientToken ?? keyFor(planRequestIdentity(unstamped, carried)),
			};
			const body = carried ? planActionFormData(request, carried) : request;

			const { data } = await axiosInstance.post<PlanActionOut>(planActionsUrl(houseId), body);
			return data;
		},
		onSuccess: (_receipt, { node, intent }) => {
			attemptRef.current = null;
			const resolveToken = node.action_target?.resolve_token;
			// The action receipt means its decision-log row is durable already, so refresh this card's
			// timeline immediately. It is a separate read from the plan and does not need the plan's
			// delayed materialisation retry.
			if (houseId !== undefined && resolveToken) {
				void queryClient.invalidateQueries({
					queryKey: getCardTimelineV2HousesHouseIdMaintenanceTimelineGetQueryKey({
						path: { house_id: houseId },
						query: { resolve_token: resolveToken },
					}),
				});
			}
			const discardedQueryKey =
				houseId === undefined
					? undefined
					: getDiscardedCardsV2HousesHouseIdMaintenanceDiscardedGetInfiniteQueryKey({
							path: { house_id: houseId },
							query: { limit: DISCARDED_PAGE_LIMIT },
						});
			// A removal adds a card to the archive. Marked stale rather than refetched: the archive is
			// read when it is opened, not while the plan is on screen.
			if (discardedQueryKey && ARCHIVING_ACTIONS.has(intent.action)) {
				void queryClient.invalidateQueries({ queryKey: discardedQueryKey, refetchType: 'none' });
			}
			// Restore removes a card from the archive as well as putting it back on the plan. The two
			// reads are deliberately separate, so neither invalidation can stand in for the other.
			if (discardedQueryKey && intent.action === PlanActions.Restore) {
				// The write receipt is enough to know this card no longer belongs in the archive. Remove it
				// synchronously so the successful control cannot fall idle on a card that should be gone.
				queryClient.setQueriesData<InfiniteData<DiscardedCardsResponse>>(
					{ queryKey: discardedQueryKey },
					(current) => {
						if (!current) return current;
						let removedCount = 0;
						const pages = current.pages.map((page) => {
							if (!page.cards) return page;
							const cards = page.cards.filter((card) => card.node_id !== node.node_id);
							removedCount += page.cards.length - cards.length;
							return cards.length === page.cards.length ? page : { ...page, cards };
						});
						if (removedCount === 0) return current;
						return {
							...current,
							pages: pages.map((page) => ({
								...page,
								page_info: {
									...page.page_info,
									total_count: Math.max(0, page.page_info.total_count - removedCount),
								},
							})),
						};
					},
				);
				// Mark the list stale for its next mount/focus without immediately downloading every page
				// again. The receipt is authoritative for this removal, and a same-tick refetch could even
				// reinsert it if the archive read model has not caught up yet.
				void queryClient.invalidateQueries({
					queryKey: discardedQueryKey,
					refetchType: 'none',
				});
			}
			if (refreshOnSuccess) refreshPlan();
		},
	});

	const { mutateAsync } = mutation;

	/**
	 * Runs the action and reports whether it landed, so a sheet can close on success and stay open
	 * on failure with the draft the user typed still in it.
	 */
	const runAction = useCallback(
		async (
			node: PlanNodeOut,
			intent: PlanActionIntent,
			evidence?: PlanActionEvidence,
			/** This request's idempotency key. Omitted, the hook mints and holds one; see above. */
			clientToken?: string,
		): Promise<boolean> => {
			try {
				await mutateAsync({ node, intent, evidence, clientToken });
				return true;
			} catch (initialError) {
				const customEntryId = node.action_target?.write_anchor?.custom_entry_id;
				if (
					houseId !== undefined &&
					customEntryId &&
					clientToken === undefined &&
					isPreWriteRejection(initialError)
				) {
					try {
						// A custom entry is stored before its occurrence projection is fully settled. The first
						// card can therefore reach the UI with an action token the resolver does not own yet.
						// A 422 guarantees that request was rejected before writing, so it is safe to fetch the
						// canonical card and try its current token. Network and server failures are deliberately
						// excluded: their outcome is ambiguous, and retargeting could action a recurring task's
						// next occurrence after the first request actually landed.
						const latestPlan = await queryClient.fetchQuery({
							...getMaintenancePlanV2HousesHouseIdMaintenancePlanGetOptions({
								path: { house_id: houseId },
								// Use a generous bounded page so a newly created far-future task is not
								// missed merely because it fell beyond the endpoint's default first page.
								query: { limit: 1000 },
							}),
							staleTime: 0,
						});
						let latestNode: PlanNodeOut | undefined;
						for (const candidate of walk(planNodes(latestPlan))) {
							if (candidate.action_target?.write_anchor?.custom_entry_id === customEntryId) {
								latestNode = candidate;
								break;
							}
						}
						// The empty client token is only an actionability probe; mutateAsync builds the
						// request again and stamps it with the real idempotency token before sending.
						if (latestNode && planActionRequest(latestNode, intent, '')) {
							await mutateAsync({ node: latestNode, intent, evidence, clientToken });
							return true;
						}
					} catch (retryError) {
						logger.error(`Plan action "${intent.action}" retry failed`, retryError);
					}
				}

				logger.error(`Plan action "${intent.action}" failed`, initialError);
				toaster.create({
					title: 'That did not save. Please try again.',
					description: import.meta.env.DEV ? refusalDetail(initialError) : undefined,
					type: 'error',
				});
				return false;
			}
		},
		[houseId, mutateAsync, queryClient],
	);

	return { runAction, isPending: mutation.isPending };
};
